/**
 * Cognito OIDC configuration for the legacy PixelMe app (`front/`).
 *
 * This app and the v2 app (`frontend/`) are served from the same origin -
 * `front/` at `/` and `frontend/` at `/v2/*` - so they deliberately share a
 * single Cognito session. oidc-client-ts keys its stored user as
 * `oidc.user:<authority>:<client_id>` in whichever store it is given, so using
 * the same issuer, the same `pixelme_spa` client id and the same
 * sessionStorage-backed store as `frontend/src/providers/auth-provider.tsx`
 * means a user who signs in on either app is already signed in on the other.
 * Navigating between `/campaigns` and `/v2/dashboard` costs no redirect.
 *
 * Keep the authority, client id, scopes, store choice and `redirect_uri` in
 * sync with `frontend/src/providers/auth-provider.tsx`. Diverging on issuer,
 * client id, scopes or store silently splits the session into two. Diverging
 * on `redirect_uri` requires a second Cognito callback registration and a
 * second code-exchange implementation.
 *
 * Both apps complete the authorization-code exchange at `/v2/sign-in` (the v2
 * app). After tokens are stored, v2 either `navigate()`s to a v2 route or
 * full-page-returns to a legacy path such as `/campaigns`. `/sign-in` in this
 * app is only the unauthenticated entry that *starts* authorize, not the
 * callback. `redirect_uri` is not part of the storage key, so this does not
 * break session sharing.
 *
 * Unlike `frontend/`, which is Vite and bakes `VITE_*` in at build time, this
 * app reads runtime config from `window.env` (populated by `env-config.js`,
 * generated from the Helm ConfigMap by `docker/front/env.sh`). Values can
 * therefore be changed with a ConfigMap edit and a pod restart - no rebuild.
 */
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

/** Shared with `frontend/`. Cognito returns the authorization code here. */
export const SIGN_IN_CALLBACK_PATH = '/v2/sign-in';

function readEnv(key) {
  if (typeof window === 'undefined' || !window.env) {
    return '';
  }

  return window.env[key] || '';
}

export function getCognitoIssuer() {
  return readEnv('REACT_APP_COGNITO_ISSUER');
}

export function getCognitoClientId() {
  return readEnv('REACT_APP_COGNITO_CLIENT_ID');
}

export function getPixelMeApiScope() {
  return readEnv('REACT_APP_PIXELME_API_SCOPE');
}

export function getHubBaseUrl() {
  return readEnv('REACT_APP_HUB_BASE_URL');
}

/**
 * `aws.cognito.signin.user.admin` is requested only because navbar-api uses it
 * as its marker scope (COGNITO_EXPECTED_SCOPE), and both hub-hub and the v2 app
 * already request it for that reason. This app has to match or navbar-api
 * rejects the token and the shared navbar does not render.
 *
 * It is not otherwise needed, and it is not free: it authorizes the bearer to
 * call Cognito's user-admin APIs against the signed-in user's own account. The
 * pool already defines a dedicated navbar resource server which would give
 * navbar-api the same marker without that capability; moving to it is tracked
 * as follow-up work and would let all three clients drop this scope.
 */
export function getCognitoScopes() {
  return ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin', getPixelMeApiScope()]
    .filter(Boolean)
    .join(' ');
}

/**
 * The sessionStorage key oidc-client-ts uses for the stored user. Mirrors
 * `UserManager#_userStoreKey` plus `WebStorageStateStore`'s default `oidc.`
 * prefix. Used to read the session synchronously from non-React code that
 * cannot await the provider - see `auth/tokenProvider.js`.
 */
export function getOidcUserStorageKey() {
  return `oidc.user:${getCognitoIssuer()}:${getCognitoClientId()}`;
}

/**
 * Builds the UserManager settings.
 *
 * Called lazily (via `getUserManager`) rather than evaluated at module load,
 * so that `window.env` (injected by a separate script tag in index.html) is
 * guaranteed to be populated first.
 */
export function buildOidcConfig() {
  // Keep OIDC transaction + session state out of localStorage so it is scoped
  // to the current tab and discarded when the tab closes. This also matches
  // `frontend/`, which is what makes the session shared rather than duplicated.
  const store = window.sessionStorage;

  return {
    authority: getCognitoIssuer(),
    client_id: getCognitoClientId(),
    redirect_uri: `${window.location.origin}${SIGN_IN_CALLBACK_PATH}`,
    response_type: 'code',
    scope: getCognitoScopes(),
    automaticSilentRenew: true,
    monitorSession: false,
    userStore: new WebStorageStateStore({ store }),
    stateStore: new WebStorageStateStore({ store }),
  };
}

let userManager = null;

/**
 * The one UserManager for this bundle.
 *
 * Shared by the React provider (`index.js` passes it as `userManager`) and by
 * non-React token consumers (`auth/tokenProvider.js`), so both read the same
 * in-memory state and the same storage. That replaces the old
 * `AuthTokenBridge`, which handed a hook closure to non-React code and, while
 * the provider was still loading, made every early API call look like "no
 * session" and start a silent iframe or a full authorize redirect.
 */
export function getUserManager() {
  if (!userManager) {
    userManager = new UserManager(buildOidcConfig());
  }

  return userManager;
}
